import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Button, message, Space, Tooltip } from 'antd';
import ReactJson, { InteractionProps } from 'react-json-view';
import './TrainingProjectJsonEditor.less';

const clipboardUnavailableTooltip = 'Clipboard access is not available in this browser.';

const parseJson = (fieldLabel: string, rawValue?: string): Record<string, unknown> => {
    if (!rawValue) {
        return {};
    }

    try {
        return JSON.parse(rawValue) as Record<string, unknown>;
    } catch (error) {
        console.error('JSON parse error: ', error);
        message.error(`JSON parse error for ${fieldLabel}: ${error}`);
        return {};
    }
};

const stringifyJson = (value: Record<string, unknown>): string => JSON.stringify(value, null, 2);

// Ant Design treats children with `value`/`onChange` props as controlled inputs. We mirror the
// incoming string in local state and, after every tree edit, emit the updated JSON string through
// `onChange` so the form field stays in sync.
type Props = {
    fieldLabel: string;
    value?: string;
    lastSavedValue?: string;
    onChange?: (value: string) => void;
};

const TrainingProjectJsonEditor: React.FC<Props> = ({ value, fieldLabel, lastSavedValue, onChange }) => {
    const [jsonObject, setJsonObject] = React.useState<Record<string, unknown>>({});
    const [clipboardSupported, setClipboardSupported] = React.useState(false);
    const [isCollapsedView, setIsCollapsedView] = React.useState(false);

    const emitChange = React.useCallback(
        (nextObject: Record<string, unknown>) => {
            const nextValue = stringifyJson(nextObject);
            setJsonObject(nextObject);
            onChange?.(nextValue);
        },
        [onChange]
    );

    const handleTreeChange = React.useCallback(
        ({ updated_src }: InteractionProps) => {
            emitChange(updated_src as Record<string, unknown>);
            return true;
        },
        [emitChange]
    );

    const applyJsonSnippet = React.useCallback(
        (rawJson: string, successMessage: string) => {
            if (!rawJson) {
                message.warning('Clipboard is empty.');
                return;
            }

            try {
                const parsedContent = JSON.parse(rawJson) as Record<string, unknown>;
                emitChange(parsedContent);
                message.success(successMessage);
            } catch (error) {
                console.error('JSON parse error: ', error);
                message.error('Clipboard content is not valid JSON.');
            }
        },
        [emitChange]
    );

    const handlePasteFromClipboard = React.useCallback(async () => {
        if (!navigator.clipboard) {
            message.error(clipboardUnavailableTooltip);
            return;
        }

        try {
            const clipboardContent = await navigator.clipboard.readText();
            applyJsonSnippet(clipboardContent, 'JSON pasted from clipboard.');
        } catch (error) {
            console.error('Clipboard read error: ', error);
            message.error('Clipboard content is not available.');
        }
    }, [applyJsonSnippet]);

    const handleCopyToClipboard = React.useCallback(async () => {
        if (!navigator.clipboard) {
            message.error(clipboardUnavailableTooltip);
            return;
        }

        try {
            const jsonString = stringifyJson(jsonObject);
            await navigator.clipboard.writeText(jsonString);
            message.success('JSON copied to clipboard.');
        } catch (error) {
            console.error('Clipboard write error: ', error);
            message.error('Unable to copy JSON to clipboard.');
        }
    }, [jsonObject]);

    const handleRevertToSaved = React.useCallback(() => {
        if (!lastSavedValue) {
            message.info('There is no saved JSON to revert to.');
            return;
        }

        try {
            const parsed = parseJson(fieldLabel, lastSavedValue);
            emitChange(parsed);
            message.success('Reverted to last saved JSON.');
        } catch (error) {
            console.error('Revert error: ', error);
            message.error('Saved JSON is not valid.');
        }
    }, [emitChange, fieldLabel, lastSavedValue]);

    const handlePasteShortcut = React.useCallback(
        (event: React.ClipboardEvent<HTMLDivElement>) => {
            if (!event.clipboardData) {
                return;
            }

            const clipboardContent = event.clipboardData.getData('text');

            if (!clipboardContent) {
                return;
            }

            event.preventDefault();
            applyJsonSnippet(clipboardContent, 'JSON pasted.');
        },
        [applyJsonSnippet]
    );

    React.useEffect(() => {
        setJsonObject(parseJson(fieldLabel, value));
    }, [value, fieldLabel]);

    React.useEffect(() => {
        setClipboardSupported(typeof navigator !== 'undefined' && Boolean(navigator.clipboard));
    }, []);

    return (
        <div className="training-project-json-editor" onPaste={handlePasteShortcut}>
            <div className="training-project-json-editor-controls">
                <Space size={8}>
                    <Tooltip
                        title={
                            clipboardSupported
                                ? 'You can also paste JSON directly with Ctrl+V (Windows/Linux) or Cmd+V (Mac)'
                                : clipboardUnavailableTooltip
                        }
                    >
                        <Button size="small" onClick={handlePasteFromClipboard} disabled={!clipboardSupported}>
                            Paste JSON
                        </Button>
                    </Tooltip>
                    <Tooltip title={clipboardSupported ? undefined : clipboardUnavailableTooltip}>
                        <Button size="small" onClick={handleCopyToClipboard} disabled={!clipboardSupported}>
                            Copy JSON
                        </Button>
                    </Tooltip>
                    <Button size="small" onClick={() => setIsCollapsedView(prev => !prev)}>
                        {isCollapsedView ? 'Expand view' : 'Minify view'}
                    </Button>
                    <Tooltip title="Restore the most recently saved JSON">
                        <Button size="small" onClick={handleRevertToSaved} disabled={!lastSavedValue}>
                            Revert to last saved
                        </Button>
                    </Tooltip>
                </Space>
            </div>

            <ReactJson
                key={isCollapsedView ? 'collapsed' : 'expanded'}
                src={jsonObject}
                name={false}
                displayDataTypes={false}
                displayObjectSize={false}
                enableClipboard={false}
                iconStyle="circle"
                collapsed={isCollapsedView}
                indentWidth={2}
                onAdd={handleTreeChange}
                onEdit={handleTreeChange}
                onDelete={handleTreeChange}
            />
        </div>
    );
};

export default observer(TrainingProjectJsonEditor);
